import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { GqlQueryResult } from '.';
import { FlyFreelyConstants } from '../constants';
import {
    AircraftRegisterEntryDetailsDto,
    AssignMaintenanceScheduleCommand,
    AssociatedResourceCommand,
    RpaDetailsDto,
    RpaDto,
    CreateRpaCommand,
    DisposeResourceCommand,
    FlightHistorySummaryDto,
    MaintenanceScheduleAssignmentDto,
    MarkResourceServiceableCommand,
    MarkResourceUnserviceableCommand,
    ResourceUnderMaintenanceCommand,
    ResultDto,
    RetireResourceCommand,
    ServiceabilitySignoffDto,
    TotalInServiceTime,
    TransferResourceCommand,
    UpdateRpaCommand,
    UpdateCustodianCommand,
    UpdateMaintenanceOrganisationCommand,
    UpdateNotesCommand,
    UpdateRpaSerialNumberCommand,
    RpaCriteria
} from '../model/api';
import { AttachmentHandler } from './attachments';
import {
    ChangeEvent,
    entityChanged,
    entityDeleted,
    entityTransferred
} from './changes';
import { FlightHistoryEntry } from './flightHistory';
import { httpParamSerializer } from './service.helpers';

export const AIRCRAFT_CHANGED_EVENT = 'aircraft-changed';

export const RPA_STATUSES = Object.freeze([
    { value: 'SERVICEABLE|UNDER_MAINTENANCE|UNSERVICEABLE', name: 'Current' },
    { value: 'SERVICEABLE', name: 'Serviceable' },
    { value: 'UNSERVICEABLE', name: 'Unserviceable' },
    { value: 'UNDER_MAINTENANCE', name: 'Under maintenance' },
    { value: 'RETIRED', name: 'Retired' },
    { value: 'DISPOSED', name: 'Disposed' }
]);

export interface RpaFlightHistoryEntry extends FlightHistoryEntry {
    serviceabilitySignoff: {
        signoffPerson?: {
            firstName: string;
            lastName: string;
        };
        serviceability: ServiceabilitySignoffDto.Serviceability;
    };
}

@Injectable({
    providedIn: 'root'
})
export class CraftService {
    private baseUrl: string;
    private changeSource = new Subject<ChangeEvent<RpaDetailsDto>>();
    change$ = this.changeSource.asObservable();

    constructor(
        constants: FlyFreelyConstants,
        private http: HttpClient,
        private apollo: Apollo
    ) {
        this.baseUrl = constants.API_URL;
    }

    ngOnDestroy() {
        this.changeSource.complete();
    }

    private notifyAircraftChanged(updatedCraft: RpaDetailsDto) {
        this.changeSource.next(entityChanged(updatedCraft));
    }

    createCraft(craftDetails: CreateRpaCommand) {
        return this.http
            .post<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts`,
                craftDetails
            )
            .pipe(tap(aircraft => this.notifyAircraftChanged(aircraft)));
    }

    updateCraft(craftId: number, craftDetails: UpdateRpaCommand) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}`,
                craftDetails
            )
            .pipe(tap(aircraft => this.notifyAircraftChanged(aircraft)));
    }

    updateInternalSerialNumber(
        craftId: number,
        command: UpdateRpaSerialNumberCommand
    ) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}/internalSerialNumber`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    findCrafts(organisationId: number, criteria?: RpaCriteria) {
        return this.http.get<RpaDto[]>(`${this.baseUrl}/webapi/crafts`, {
            params: httpParamSerializer({ ...criteria, organisationId })
        });
    }

    findCraftsById(ids: Array<number>, managingOrganisationId: number) {
        if (!ids) {
            return Promise.resolve([]);
        }
        return Promise.all(
            ids.map(id => this.findById(id, managingOrganisationId))
        );
    }

    findById(craftId: number, managingOrganisationId: number) {
        return this.http.get<RpaDetailsDto>(
            `${this.baseUrl}/webapi/crafts/${craftId}`,
            {
                params: httpParamSerializer({
                    managingOrganisationId
                })
            }
        );
    }

    findRpaFlightHistory(
        criteria: {
            id: number;
            managingOrganisationId: number;
            includeType: ('LOGGED' | 'HISTORICAL' | 'UNASSIGNED')[];
        },
        order: 'ASC' | 'DESC',
        page = 0,
        pageSize = 10
    ) {
        return this.apollo
            .query<{
                findRpaFlightHistory: GqlQueryResult<RpaFlightHistoryEntry>;
            }>({
                query: gql`
                    query find(
                        $criteria: ResourceFlightHistoryCriteria
                        $order: Direction
                        $pageSize: Int
                        $page: Int
                    ) {
                        findRpaFlightHistory(
                            criteria: $criteria
                            order: $order
                            page: $page
                            pageSize: $pageSize
                        ) {
                            count
                            results {
                                id
                                type
                                missionId
                                flightNumber
                                startTime
                                endTime
                                duration
                                visualLineOfSight
                                timeOfDay
                                rpaId
                                serviceabilitySignoff {
                                    signoffPerson {
                                        firstName
                                        lastName
                                    }
                                    serviceability
                                }
                                rpaType {
                                    id
                                    make
                                    model
                                    rpaCategory
                                    performanceSpecifications {
                                        minTakeOffWeight
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria,
                    order,
                    page,
                    pageSize
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => ({
                    ...r.data.findRpaFlightHistory,
                    pagination: { pageSize, page }
                }))
            );
    }

    findFlightHistorySummary(craftId: number, managingOrganisationId?: number) {
        return this.http.get<FlightHistorySummaryDto>(
            `${this.baseUrl}/webapi/crafts/${craftId}/history/summary`,
            {
                params: httpParamSerializer({
                    managingOrganisationId
                })
            }
        );
    }

    findTotalTimeInService(
        craftId: number,
        managingOrganisationId: number,
        until?: string
    ) {
        return this.http.get<TotalInServiceTime>(
            `${this.baseUrl}/webapi/crafts/${craftId}/totalTimeInService`,
            {
                params: httpParamSerializer({
                    managingOrganisationId,
                    until
                })
            }
        );
    }

    findAircraftRegisterEntryByAircraftId(
        craftId: number,
        managingOrganisationId: number,
        at: string
    ) {
        return this.http.get<AircraftRegisterEntryDetailsDto[]>(
            `${this.baseUrl}/webapi/crafts/${craftId}/authorityRegisters`,
            { params: httpParamSerializer({ at, managingOrganisationId }) }
        );
    }

    deleteCraft(craftId: number) {
        return this.http
            .delete(`${this.baseUrl}/webapi/crafts/${craftId}`)
            .pipe(tap(() => this.changeSource.next(entityDeleted(craftId))));
    }

    attachmentHandler(craftId: number, managingOrganisationId?: number) {
        return new AttachmentHandler(
            this.http,
            `/webapi/crafts/${craftId}/attachments`,
            null,
            managingOrganisationId
        );
    }

    getStatuses() {
        return RPA_STATUSES;
    }

    markAircraftUnderMaintenance(
        craftId: number,
        command: ResourceUnderMaintenanceCommand
    ) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}/underMaintenance`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    markAircraftActive(
        craftId: number,
        command: MarkResourceServiceableCommand
    ) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}/active`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    markAircraftRetire(craftId: number, command: RetireResourceCommand) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}/retire`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    markAircraftGrounded(
        craftId: number,
        command: MarkResourceUnserviceableCommand
    ) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}/ground`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    disposeRpa(craftId: number, command: DisposeResourceCommand) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${craftId}/dispose`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    exportOrganisationRpa(organisationId: number, authorityTypeIds?: number[]) {
        const headers = new HttpHeaders({
            'Content-Type': 'text/csv;charset=utf-8',
            Accept: 'text/csv;charset=utf-8'
        });
        return this.http.get(`${this.baseUrl}/webapi/rpa/export`, {
            params: httpParamSerializer({
                organisationId,
                authorityTypeId: authorityTypeIds
            }),
            responseType: 'blob',
            headers: headers
        });
    }

    assignMaintenanceSchedule(
        rpaId: number,
        command: AssignMaintenanceScheduleCommand
    ) {
        return this.http
            .put<MaintenanceScheduleAssignmentDto>(
                `${this.baseUrl}/webapi/crafts/${rpaId}/maintenanceSchedule`,
                command
            )
            .pipe(tap(() => this.changeSource.next(null)));
    }

    addAssociatedEquipment(rpaId: number, command: AssociatedResourceCommand) {
        return this.http
            .post<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${rpaId}/equipment`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    removeAssociatedEquipment(
        rpaId: number,
        command: AssociatedResourceCommand
    ) {
        return this.http
            .delete<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${rpaId}/equipment/${command.resourceId}`,

                {
                    params: httpParamSerializer({
                        managingOrganisationId: command.managingOrganisationId
                    })
                }
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    addAssociatedBatterySet(rpaId: number, command: AssociatedResourceCommand) {
        return this.http
            .post<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${rpaId}/batterySets`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    removeAssociatedBatterySet(
        rpaId: number,
        command: AssociatedResourceCommand
    ) {
        return this.http
            .delete<RpaDetailsDto>(
                `${this.baseUrl}/webapi/crafts/${rpaId}/batterySets/${command.resourceId}`,

                {
                    params: httpParamSerializer({
                        managingOrganisationId: command.managingOrganisationId
                    })
                }
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    updateCraftNotes(rpaId: number, command: UpdateNotesCommand) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/rpa/${rpaId}/notes`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    updateCustodian(rpaId: number, command: UpdateCustodianCommand) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/rpa/${rpaId}/custodian`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    updateMaintenanceOrganisation(
        rpaId: number,
        command: UpdateMaintenanceOrganisationCommand
    ) {
        return this.http
            .put<RpaDetailsDto>(
                `${this.baseUrl}/webapi/rpa/${rpaId}/maintenanceOrganisation`,
                command
            )
            .pipe(tap(rpa => this.notifyAircraftChanged(rpa)));
    }

    transfer(rpaId: number, command: TransferResourceCommand) {
        return this.http
            .post<ResultDto>(
                `${this.baseUrl}/webapi/rpa/${rpaId}/transfer`,
                command
            )
            .pipe(tap(() => this.changeSource.next(entityTransferred(rpaId))));
    }

    onCraftChanged() {
        this.changeSource.next(null);
    }
}
